import { clsx } from 'clsx';

import { Icon, IconButton } from '@/ui';

import { buildCalendarGrid } from './buildCalendarGrid';
import {
  COMFORTABLE_DAY_HEADERS,
  DAY_HEADER_LABELS,
  DAY_HEADERS,
} from './constants';

import type { FC } from 'react';

interface MentoCalDatePickerProps {
  comfortable?: boolean;
  days: { date: string; enabled: boolean }[];
  selectedDate: string;
  onSelectDate: (date: string) => void;
  onShowMore?: () => void;
  canShowMore?: boolean;
  isLoadingMore?: boolean;
  todayIso?: string;
  onPrevMonth?: () => void;
  onNextMonth?: () => void;
}

const localTodayIso = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const MentoCalDatePicker: FC<MentoCalDatePickerProps> = ({
  comfortable = false,
  days,
  selectedDate,
  onSelectDate,
  onShowMore,
  canShowMore = false,
  isLoadingMore = false,
  todayIso = localTodayIso(),
  onPrevMonth,
  onNextMonth,
}) => {
  const { monthLabel, months } = buildCalendarGrid(days);
  const dayHeaders = comfortable ? COMFORTABLE_DAY_HEADERS : DAY_HEADERS;
  const yearSeparator = monthLabel.lastIndexOf(' ');
  const month = monthLabel.slice(0, yearSeparator);
  const year = monthLabel.slice(yearSeparator + 1);

  return (
    <div className="flex flex-col">
      <div className="mb-2 flex items-center justify-between">
        {comfortable ? (
          <span className="flex gap-1 text-base font-medium">
            <span className="text-cement-500">{month}</span>
            <span className="text-cement-300">{year}</span>
          </span>
        ) : (
          <span className="text-base font-medium text-cement-500">
            {monthLabel}
          </span>
        )}
        {(onPrevMonth ?? onNextMonth) ? (
          <div className="flex items-center gap-1">
            {onPrevMonth ? (
              <IconButton
                aria-label="Previous month"
                className="p-1"
                onClick={onPrevMonth}
              >
                <Icon>chevron_left</Icon>
              </IconButton>
            ) : null}
            {onNextMonth ? (
              <IconButton
                aria-label="Next month"
                className="p-1"
                onClick={onNextMonth}
              >
                <Icon>chevron_right</Icon>
              </IconButton>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-7 text-center text-xs font-medium text-cement-400">
        {dayHeaders.map((header, i) => (
          <span
            key={`${header}-${String(i)}`}
            className={clsx(
              'flex items-center justify-center',
              comfortable ? 'h-10' : 'h-7',
            )}
          >
            <span aria-hidden="true">{header}</span>
            <span className="sr-only">{DAY_HEADER_LABELS[i]}</span>
          </span>
        ))}
      </div>

      <div className="flex flex-col gap-2.5" data-testid="mentocal-date-grid">
        {months.map((calendarMonth) => (
          <div
            key={calendarMonth.key}
            className={clsx('flex flex-col', comfortable ? 'gap-1' : 'gap-0')}
            data-testid="mentocal-month"
          >
            {calendarMonth.weeks.map((week, wi) => (
              <div
                key={`week-${calendarMonth.key}-${String(wi)}`}
                className={clsx(
                  'grid grid-cols-7',
                  comfortable ? 'gap-1' : 'gap-0',
                )}
              >
                {week.map((cell, ci) => {
                  if (!cell) {
                    return (
                      <span
                        key={`empty-${calendarMonth.key}-${String(wi)}-${String(ci)}`}
                        className={clsx(
                          'aspect-square w-full',
                          comfortable ? 'max-w-11' : 'max-w-8',
                        )}
                      />
                    );
                  }

                  const isSelected = cell.date === selectedDate;
                  const isToday = !isSelected && cell.date === todayIso;
                  const fullDateLabel = new Date(
                    `${cell.date}T00:00:00`,
                  ).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  });

                  return (
                    <button
                      key={cell.date}
                      aria-label={fullDateLabel}
                      aria-pressed={isSelected}
                      className={clsx(
                        'mx-auto flex aspect-square w-full items-center justify-center rounded-lg border-0 text-sm font-medium',
                        comfortable ? 'max-w-11' : 'max-w-8',
                        isSelected && cell.enabled && 'bg-sage-500 text-white',
                        !isSelected &&
                          cell.enabled &&
                          (comfortable
                            ? 'bg-sage-100 text-cement-500'
                            : 'bg-white text-cement-500'),
                        !cell.enabled && 'bg-transparent text-cement-300',
                        isToday && 'border border-cement-400',
                        cell.enabled ? 'cursor-pointer' : 'cursor-default',
                      )}
                      data-today={isToday ? 'true' : undefined}
                      disabled={!cell.enabled}
                      type="button"
                      onClick={() => {
                        onSelectDate(cell.date);
                      }}
                    >
                      {cell.day}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        ))}
      </div>

      {canShowMore ? (
        <button
          className="mt-4 self-center border-0 bg-transparent text-sm font-medium text-sage-500 disabled:opacity-40"
          disabled={isLoadingMore}
          type="button"
          onClick={onShowMore}
        >
          Show More
        </button>
      ) : null}
    </div>
  );
};
